import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Upload, 
  FileText, 
  Trash2, 
  CheckCircle, 
  AlertCircle,
  Lock,
  LogIn,
  BarChart2,
  Calendar,
  TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [reports, setReports] = useState([]);
  const [yearlyStats, setYearlyStats] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({});
  const [loading, setLoading] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December', 'Yearly'
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setReports(data.reports || []);
        setYearlyStats(data.yearly_stats);
        localStorage.setItem('adminAuth', btoa(`${credentials.username}:${credentials.password}`));
      } else {
        setAuthError('Invalid username or password');
      }
    } catch (error) {
      setAuthError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getAuthHeader = () => {
    const savedAuth = localStorage.getItem('adminAuth');
    if (savedAuth) {
      return { 'Authorization': 'Basic ' + savedAuth };
    }
    return { 'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`) };
  };

  const handleFileUpload = async (month, file) => {
    const monthKey = month.toLowerCase();
    setUploadStatus(prev => ({ ...prev, [monthKey]: 'uploading' }));

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/admin/upload-report/${monthKey}`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData
      });

      if (response.ok) {
        setUploadStatus(prev => ({ ...prev, [monthKey]: 'success' }));
        fetchReports();
        setTimeout(() => {
          setUploadStatus(prev => ({ ...prev, [monthKey]: null }));
        }, 3000);
      } else {
        setUploadStatus(prev => ({ ...prev, [monthKey]: 'error' }));
      }
    } catch (error) {
      setUploadStatus(prev => ({ ...prev, [monthKey]: 'error' }));
    }
  };

  const handleDeleteReport = async (month) => {
    const monthKey = month.toLowerCase();
    if (!window.confirm(`Are you sure you want to delete the ${month} report?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/delete-report/${monthKey}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });

      if (response.ok) {
        fetchReports();
      }
    } catch (error) {
      console.error('Error deleting report:', error);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: getAuthHeader()
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
        setYearlyStats(data.yearly_stats);
      } else if (response.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('adminAuth');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const isReportUploaded = (month) => {
    return reports.some(r => r.month === month.toLowerCase());
  };

  const getReportInfo = (month) => {
    return reports.find(r => r.month === month.toLowerCase());
  };

  // Check for saved auth on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem('adminAuth');
    if (savedAuth) {
      fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': 'Basic ' + savedAuth }
      })
        .then(res => {
          if (res.ok) {
            res.json().then(data => {
              setIsAuthenticated(true);
              setReports(data.reports || []);
              setYearlyStats(data.yearly_stats);
            });
          } else {
            localStorage.removeItem('adminAuth');
          }
        })
        .catch(() => localStorage.removeItem('adminAuth'));
    }
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center px-6 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-strong rounded-2xl p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-14 h-14 rounded-xl bg-[#8B5CF6]/15 flex items-center justify-center">
                <Lock className="w-7 h-7 text-[#8B5CF6]" />
              </div>
            </div>
            
            <h1 className="font-heading font-bold text-2xl text-white text-center mb-2">Admin Login</h1>
            <p className="font-body text-white/50 text-sm text-center mb-6">Enter your credentials to access the admin panel</p>

            {authError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="font-body text-sm text-red-400">{authError}</span>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="font-body text-xs text-white/50 mb-1 block">Username</label>
                <input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white font-body focus:outline-none focus:border-[#8B5CF6]/50"
                  placeholder="Enter username"
                  required
                  data-testid="admin-username-input"
                />
              </div>
              
              <div>
                <label className="font-body text-xs text-white/50 mb-1 block">Password</label>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white font-body focus:outline-none focus:border-[#8B5CF6]/50"
                  placeholder="Enter password"
                  required
                  data-testid="admin-password-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-purple flex items-center justify-center gap-2 py-3"
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn size={16} />
                    Sign In
                  </>
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050508] pt-24 pb-12 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading font-bold text-2xl text-white mb-1">Admin Dashboard</h1>
              <p className="font-body text-white/50 text-sm">Manage PDF reports and performance data</p>
            </div>
            <button
              onClick={() => {
                setIsAuthenticated(false);
                localStorage.removeItem('adminAuth');
              }}
              className="btn-secondary text-sm"
              data-testid="admin-logout-btn"
            >
              Logout
            </button>
          </div>

          {/* Stats Overview */}
          {yearlyStats && (
            <div className="performance-card p-6 mb-8">
              <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#10B981]" />
                2025 Performance Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 rounded-lg bg-white/3">
                  <div className="font-heading font-bold text-xl text-[#10B981]">+{yearlyStats.compounded_return_pct}%</div>
                  <div className="font-body text-[10px] text-white/40">Total Return</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-white/3">
                  <div className="font-heading font-bold text-xl text-[#007AFF]">{yearlyStats.compounded_win_rate_pct}%</div>
                  <div className="font-body text-[10px] text-white/40">Win Rate</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-white/3">
                  <div className="font-heading font-bold text-xl text-[#8B5CF6]">{yearlyStats.profit_factor}</div>
                  <div className="font-body text-[10px] text-white/40">Profit Factor</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-white/3">
                  <div className="font-heading font-bold text-xl text-white">{yearlyStats.compounded_trades}</div>
                  <div className="font-body text-[10px] text-white/40">Total Trades</div>
                </div>
              </div>
            </div>
          )}

          {/* Reports Upload Section */}
          <div className="performance-card p-6">
            <h3 className="font-heading font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#8B5CF6]" />
              PDF Report Management
            </h3>
            <p className="font-body text-white/50 text-sm mb-6">
              Upload monthly PDF reports (January-December) and the full yearly report. Reports will be available for download on the main site.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {months.map((month) => {
                const monthKey = month.toLowerCase();
                const isUploaded = isReportUploaded(month);
                const reportInfo = getReportInfo(month);
                const status = uploadStatus[monthKey];

                return (
                  <div
                    key={month}
                    className={`p-4 rounded-lg border transition-colors ${
                      isUploaded 
                        ? 'bg-[#10B981]/10 border-[#10B981]/30' 
                        : 'bg-white/3 border-white/10'
                    }`}
                    data-testid={`report-card-${monthKey}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar className={`w-4 h-4 ${isUploaded ? 'text-[#10B981]' : 'text-white/40'}`} />
                        <span className="font-heading font-semibold text-white text-sm">{month}</span>
                      </div>
                      {isUploaded && (
                        <CheckCircle className="w-4 h-4 text-[#10B981]" />
                      )}
                    </div>

                    {isUploaded && reportInfo && (
                      <p className="font-body text-[10px] text-white/40 mb-3">
                        Uploaded: {new Date(reportInfo.uploaded_at).toLocaleDateString()}
                      </p>
                    )}

                    <div className="flex gap-2">
                      <label className={`flex-1 cursor-pointer ${status === 'uploading' ? 'pointer-events-none' : ''}`}>
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(month, file);
                          }}
                          data-testid={`upload-input-${monthKey}`}
                        />
                        <div className={`py-2 px-3 rounded-lg flex items-center justify-center gap-2 text-xs transition-colors ${
                          status === 'uploading'
                            ? 'bg-[#8B5CF6]/20 text-[#8B5CF6]'
                            : status === 'success'
                            ? 'bg-[#10B981]/20 text-[#10B981]'
                            : status === 'error'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-[#8B5CF6]/10 hover:bg-[#8B5CF6]/20 text-[#8B5CF6]'
                        }`}>
                          {status === 'uploading' ? (
                            <>
                              <div className="w-3 h-3 border-2 border-[#8B5CF6]/30 border-t-[#8B5CF6] rounded-full animate-spin" />
                              Uploading...
                            </>
                          ) : status === 'success' ? (
                            <>
                              <CheckCircle size={12} />
                              Uploaded!
                            </>
                          ) : status === 'error' ? (
                            <>
                              <AlertCircle size={12} />
                              Failed
                            </>
                          ) : (
                            <>
                              <Upload size={12} />
                              {isUploaded ? 'Replace' : 'Upload'}
                            </>
                          )}
                        </div>
                      </label>

                      {isUploaded && (
                        <button
                          onClick={() => handleDeleteReport(month)}
                          className="py-2 px-3 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                          data-testid={`delete-btn-${monthKey}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upload Summary */}
          <div className="mt-6 p-4 glass rounded-lg">
            <div className="flex items-center gap-3">
              <BarChart2 className="w-5 h-5 text-[#007AFF]" />
              <div>
                <span className="font-body text-white/60 text-sm">Reports uploaded: </span>
                <span className="font-heading font-semibold text-white">{reports.length} / 13</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
